// Learning Center library (dealer portal). Shows the live, in-scope modules for the current
// rooftop, grouped by category, with an at-a-glance completion summary. Cards link to the
// routed reader. Which modules are visible is governed by Hasura select permissions plus
// client-side tier / product-feature / effective-date scoping.

import React, { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import {
  Alert, Box, Card, CardContent, Grid, InputAdornment, LinearProgress,
  Skeleton, Stack, TextField, Typography,
} from '@mui/material';
import { BiSearch, BiBook } from 'react-icons/bi';
import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import { useLearningCenter } from '../hooks/useLearningCenter';
import { useModuleCompletion } from '../hooks/useModuleCompletion';
import ModuleCard from '../components/ModuleCard';
import NewContentAlert from '../components/NewContentAlert';
import { MODULE_FEATURES, featureLabel, type ModuleSummary, type ModuleFeature } from '../types';

/** Module go-live time in ms for sorting; unparseable/missing dates sort oldest (0). */
const beginsAtMs = (m: ModuleSummary): number => Date.parse(m.begins_at) || 0;

/** A grid of placeholder cards matching the thumbnail-forward layout, shown on first load. */
const LoadingSkeletons: React.FC = () => (
  <Grid container spacing={2}>
    {Array.from({ length: 8 }).map((_, i) => (
      <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Card sx={{ height: '100%' }}>
          <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', aspectRatio: '16 / 9' }} />
          <CardContent>
            <Skeleton width={72} height={22} sx={{ borderRadius: 4 }} />
            <Skeleton width="85%" height={24} sx={{ mt: 1 }} />
            <Skeleton width="100%" height={18} />
            <Skeleton width="60%" height={18} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const LearningCenterContent: React.FC = () => {
  // `modules` is the search-filtered view for the grid; `scopedModules` is the rooftop's full
  // catalog — the progress meter and new-content banner describe the catalog, not the search.
  const { modules, scopedModules, loading, error, search, setSearch } = useLearningCenter();
  const { completed } = useModuleCompletion();

  const grouped = useMemo(() => {
    const byFeature = new Map<ModuleFeature, ModuleSummary[]>();
    for (const m of modules) {
      const arr = byFeature.get(m.feature) ?? [];
      arr.push(m);
      byFeature.set(m.feature, arr);
    }
    // Known features first (in their canonical order), then any live feature the client enum
    // doesn't know about yet — a training authored against a product that shipped after this enum
    // was frozen still counts toward `total`/`doneCount`, so it must render a card too rather than
    // silently vanish from the grid. featureLabel falls back to the raw value for unknown features.
    const known = MODULE_FEATURES.filter(f => byFeature.has(f));
    const unknown = [...byFeature.keys()].filter(f => !MODULE_FEATURES.includes(f));
    return [...known, ...unknown].map(f => {
      const items = byFeature.get(f)!;
      // Within each category: pending trainings first (newest by go-live date), completed at the
      // back — so the next thing left to do, and the freshest of it, surfaces first.
      const pending = items
        .filter(m => !completed.has(m.key))
        .sort((a, b) => beginsAtMs(b) - beginsAtMs(a));
      const done = items.filter(m => completed.has(m.key));
      return { feature: f, items: [...pending, ...done] };
    });
  }, [modules, completed]);

  const doneCount = useMemo(
    () => scopedModules.reduce((n, m) => n + (completed.has(m.key) ? 1 : 0), 0),
    [scopedModules, completed],
  );
  const total = scopedModules.length;

  return (
    <Box sx={{ p: 3, maxWidth: 1180, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Learning Center</Typography>
          <Typography variant="body2" color="text.secondary">Short training on using the portal — watch or read in a couple of minutes.</Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search training…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><BiSearch /></InputAdornment> }}
          sx={{ minWidth: { sm: 280 } }}
        />
      </Stack>

      {/* Progress summary */}
      {total > 0 && (
        <Box sx={{ mb: 3, maxWidth: 420 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Your progress</Typography>
            <Typography variant="body2" color="text.secondary">{doneCount} of {total} completed</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            color="success"
            value={total ? (doneCount / total) * 100 : 0}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>Couldn’t load training right now. Please try again.</Alert>}

      <NewContentAlert modules={scopedModules} />

      {loading && scopedModules.length === 0 ? (
        <LoadingSkeletons />
      ) : modules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}><BiBook size={48} /></Box>
          <Typography>{search ? 'No training matches your search.' : 'No training available yet.'}</Typography>
        </Box>
      ) : (
        grouped.map(group => (
          <Box key={group.feature} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{featureLabel(group.feature)}</Typography>
            <Grid container spacing={2}>
              {group.items.map(m => (
                <Grid key={m.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ModuleCard module={m} completed={completed.has(m.key)} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Box>
  );
};

const LearningCenterPage: React.FC = () => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <DefaultErrorFallback message='Unexpected error caught at LearningCenterPage.tsx' error={error as Error} resetError={resetError} />
    )}
  >
    <LearningCenterContent />
  </Sentry.ErrorBoundary>
);

export default LearningCenterPage;
